import { useEffect, useState } from 'react';

/**
 * Whether a field's text has run past one line, and so wants more room than the controls beside it
 * are leaving it.
 *
 * `containerRef` is an element wrapping the editable rather than the editable itself: design
 * system fields own their own DOM, so the contenteditable is reached by query.
 *
 * **It latches, and clears when the field empties.** Callers use this to give the field a line of
 * its own, which is wider - so the text it tripped on reflows back onto one line, and releasing on
 * the count alone would flip the field between sizes on every keystroke.
 */
export const useTextareaLineFill = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
}) => {
  const [isNearWrap, setIsNearWrap] = useState(false);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const measure = () => {
      // Re-queried per measure rather than captured once: rich text fields
      // rebuild their own DOM, so a captured element can end up detached.
      const editable = container.querySelector<HTMLElement>(
        '[contenteditable="true"]'
      );

      if (!editable) return;

      /*
        **Latched, so the only thing left to watch for is the field emptying** - which is a text
        check and costs no layout. Everything below it forces a reflow, so the point of this branch
        is not reaching them.
      */
      if (isNearWrap) {
        if (!editable.textContent) setIsNearWrap(false);

        return;
      }

      /*
        **Rects node by node, grouped by rounded top - NOT a Range over the field.** A Range reports
        the border box of every element it fully contains, and a rich text field wraps each line in
        a block, so ranging the field counts one line however much is typed. Taken a node at a time
        the rects come back tight to the glyphs, one per line fragment, and the tops are what put a
        line's fragments back together: formatting splits a line into one node per run, and real
        fonts set those a fraction of a pixel apart.

        **`<br>` elements are walked as well as text**, because Shift+Enter makes a line with
        nothing in it for a text-only walk to find. The editor adds a second, managed `<br>` to keep
        that last line rendered, and both sit on the top they belong to, so the set absorbs the
        duplicate.
      */
      const range = document.createRange();
      const walker = document.createTreeWalker(
        editable,
        // `SHOW_ALL` rather than the two masks this wants, which would be a bitwise or; the filter
        // below is doing the choosing in any case.
        NodeFilter.SHOW_ALL,
        {
          // Skipped, not rejected: an element is not a line, but what is inside it may be.
          acceptNode: node =>
            node.nodeType === Node.TEXT_NODE || node.nodeName === 'BR'
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_SKIP,
        }
      );
      const tops = new Set<number>();

      let node = walker.nextNode();

      // Stops at two: the question is whether the text has run past one line, not how far.
      while (node && tops.size <= 1) {
        range.selectNode(node);
        /*
          `Array.from` with a map rather than a spread: a `DOMRectList` is array-like but not
          iterable, so a spread does not type - and the map argument is also what keeps the lint
          autofixer from putting one back.
        */
        Array.from(range.getClientRects(), rect =>
          Math.round(rect.top)
        ).forEach(top => {
          tops.add(top);
        });

        node = walker.nextNode();
      }

      if (tops.size > 1) setIsNearWrap(true);
    };

    measure();

    // Watched for DOM changes rather than `input` events: an `input` listener on
    // the editable runs in the target phase, ahead of React's delegated listener on
    // the root, so it measures the DOM a character behind, and rich text fields
    // preventDefault paste so no `input` fires for one at all. Mutations fire once
    // the DOM is actually updated, whatever changed it, and before the browser
    // paints.
    const mutationObserver = new MutationObserver(measure);

    /*
      The same text wraps differently in a narrower field, so the width has to be watched as well
      as the content - but only while the field can still be tripped. Once latched no width
      releases it, and this is the observer that would otherwise measure once per frame of a drag.
    */
    const resizeObserver = isNearWrap ? null : new ResizeObserver(measure);

    mutationObserver.observe(container, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    resizeObserver?.observe(container);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [containerRef, isNearWrap]);

  return { isNearWrap };
};
